import React, { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Trash2, Check, Pen } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SignaturePad Component
 * Canvas-based signature capture for check-in/check-out
 */
const SignaturePad = ({ 
  open, 
  onClose, 
  onCapture, 
  signatureType = 'check_in',
  title = 'Capture Signature',
  description,
  confirmLabel
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    if (open && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Set canvas size
      canvas.width = 400;
      canvas.height = 200;
      
      // Clear and set background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw signature line
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(20, 160);
      ctx.lineTo(380, 160);
      ctx.stroke();
      
      // Add "Sign here" text
      ctx.fillStyle = '#9ca3af';
      ctx.font = '12px sans-serif';
      ctx.fillText('Sign above the line', 160, 180);
      
      setHasSignature(false);
    }
  }, [open]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Redraw signature line
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, 160);
    ctx.lineTo(380, 160);
    ctx.stroke();
    
    ctx.fillStyle = '#9ca3af';
    ctx.font = '12px sans-serif';
    ctx.fillText('Sign above the line', 160, 180);
    
    setHasSignature(false);
  };

  const handleCapture = () => {
    if (!hasSignature) {
      toast.error('Please sign before submitting');
      return;
    }
    
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    onCapture(signatureData, signatureType);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg z-[100]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pen className="w-5 h-5 text-cyan-500" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {description || (signatureType === 'check_in' 
              ? 'Sign below to confirm your check-in for this shift'
              : 'Sign below to confirm your check-out from this shift')
            }
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Card className="border-2 border-dashed border-gray-300">
            <CardContent className="p-2">
              <canvas
                ref={canvasRef}
                className="w-full cursor-crosshair touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                data-testid="signature-canvas"
              />
            </CardContent>
          </Card>
          
          <div className="flex justify-end mt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSignature}
              className="text-gray-500"
              data-testid="clear-signature-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Clear
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleCapture}
            disabled={!hasSignature}
            className="bg-cyan-500 hover:bg-cyan-600"
            data-testid="submit-signature-btn"
          >
            <Check className="w-4 h-4 mr-2" />
            {confirmLabel || (signatureType === 'check_in' ? 'Confirm Check-In' : 'Confirm Check-Out')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SignaturePad;
